import { useEffect, useRef, useState } from "react";
import { clampColumnWidth } from "../utils/columnWidths";

// The drag affordance for one resizable <th> — modeled directly on
// FloatingWindow.jsx's resize-handle mechanics (pointer capture, rAF-
// throttled move, userSelect:none while dragging), not just similarly:
// same failure mode (a fast drag outrunning the cursor) needs the same fix.
//
// Unlike FloatingWindow, this component owns no width state itself — the
// table's useColumnWidths() hook does. `onResize` fires every frame during
// the drag (live preview), `onCommit` fires once on pointer-up/cancel (the
// only point at which the width is persisted to localStorage).
export default function ColumnResizeHandle({ startWidth, onResize, onCommit }) {
  const gesture = useRef(null); // { startX, startWidth, lastWidth }
  const frame = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation(); // must not also fire the header's own onClick (sort)
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = { startX: e.clientX, startWidth, lastWidth: startWidth };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    // rAF-throttled: pointermove fires far faster than the screen repaints.
    if (frame.current) return;
    const { clientX } = e;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const next = clampColumnWidth(g.startWidth + (clientX - g.startX));
      g.lastWidth = next;
      onResize(next);
    });
  };

  const endGesture = (e) => {
    const g = gesture.current;
    if (!g) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    gesture.current = null;
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
    setDragging(false);
    onCommit(g.lastWidth);
  };

  // While dragging, the pointer regularly crosses over cell text — without
  // this every drag selects half the row behind it.
  useEffect(() => {
    if (!dragging) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = prev;
    };
  }, [dragging]);

  useEffect(() => () => frame.current && cancelAnimationFrame(frame.current), []);

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      onClick={(e) => e.stopPropagation()}
      role="separator"
      aria-orientation="vertical"
      // The outer div is the (wider, easier-to-grab) hit target — w-3
      // (12px; widened from an 8px first pass that owner-tested as too hard
      // to land precisely) — the inner bar is the visible cue — zinc-400 at
      // rest, splitting the difference between `line` (zinc-200, too faint
      // to read at a glance) and `ink-muted` (zinc-600, the header text's
      // own colour, too dark). Full height (owner ask) — it reads as a real
      // border between columns, not a floating handle. ⚠️ The bar is pinned
      // right-0 (flush with the <th>'s true right edge), NOT centered in
      // the hit target: the header's whole-cell hover fill
      // (SortableColumnHeader.jsx) also stops at that same edge, and
      // centering the bar left a ~3px gap where the fill visibly bled past
      // it. Still fully inside the header's own px-4 (16px) padding — the
      // <th> has `truncate` (overflow:hidden) now, so anything extending
      // past its border box (e.g. a negative margin nudging it onto the
      // column boundary) gets silently clipped.
      className="group absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize touch-none select-none"
    >
      {/* A BORDER, not a 2px filled bar (owner review, Oct 27 2026): at a
          fractional zoom (175%, 180%) a filled bar painted 4 device pixels in
          some columns and 5 in others, so the column edges looked uneven.
          Border widths are rounded the same way everywhere. At rest it's the
          app's hairline divider colour; hovered or dragged it turns brand blue
          and 2px, so it still reads as something you can grab. */}
      <span
        className={`absolute right-0 top-0 bottom-0 border-r transition-colors ${
          dragging ? "border-r-2 border-brand-500" : "border-line group-hover:border-r-2 group-hover:border-brand-500"
        }`}
      />
    </div>
  );
}
